import { useEffect } from 'react';

const THUMBNAIL = '.legacy-grid .itemthumb-pic > a[data-action="openDescription"], [data-asset-hover]';

function originalSource(source: string): string {
  const url = new URL(source, window.location.href);
  // storage.preview_url writes <folder>/_thumbs/<original filename>.jpg.
  // TIFF/video previews must stay JPEG: browsers cannot display their originals.
  if (url.origin === window.location.origin) {
    url.pathname = url.pathname.replace(/\/_thumbs\/([^/]+\.(?:png|jpe?g|gif|bmp|ico|webp|avif))\.jpg$/i, '/$1');
  }
  return url.href;
}

/** One delegated listener also covers rows recycled by the virtual grids. */
export function AssetHoverPreview() {
  useEffect(() => {
    let anchor: Element | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let picture: HTMLImageElement | null = null;
    let popup: HTMLDivElement | null = null;
    let observer: MutationObserver | null = null;
    let generation = 0;

    const close = () => {
      generation++;
      clearTimeout(timer);
      if (picture) { picture.onload = null; picture.onerror = null; }
      observer?.disconnect();
      popup?.remove();
      popup = null;
      picture = null;
      anchor = null;
    };
    const over = (event: PointerEvent) => {
      if (event.pointerType === 'touch' || !(event.target instanceof Element)) return;
      const next = event.target.closest(THUMBNAIL);
      if (next === anchor) return;
      close();
      const thumbnail = next?.querySelector('img');
      if (!next || !thumbnail || document.querySelector('.modal-backdrop')) return;
      anchor = next;
      const token = generation;
      const source = thumbnail.currentSrc || thumbnail.src;
      const original = next.getAttribute('data-avatar-original') || originalSource(source);
      timer = setTimeout(() => {
        const img = new Image();
        picture = img;
        img.alt = next.getAttribute('data-asset-hover') || next.closest('.itemthumb')?.querySelector('.asset-title, .itemthumb-title-action')?.textContent || '';
        img.decoding = 'async';
        img.onload = () => {
          if (token !== generation || !next.isConnected || !next.matches(':hover')) return;
          const margin = 12;
          const scale = Math.min(1, (window.innerWidth - margin * 2) / img.naturalWidth, (window.innerHeight - margin * 2) / img.naturalHeight);
          const width = Math.floor(img.naturalWidth * scale);
          const height = Math.floor(img.naturalHeight * scale);
          const rect = next.getBoundingClientRect();
          let left = rect.right + margin;
          if (left + width > window.innerWidth - margin) left = rect.left - width - margin;
          left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
          const top = Math.max(margin, Math.min(rect.top, window.innerHeight - height - margin));
          popup = document.createElement('div');
          popup.className = 'asset-hover-preview';
          popup.setAttribute('aria-hidden', 'true');
          Object.assign(popup.style, { left: `${left}px`, top: `${top}px`, width: `${width}px`, height: `${height}px` });
          popup.append(img);
          document.body.append(popup);
        };
        img.onerror = () => {
          if (token !== generation) return;
          if (img.src !== source) img.src = source;
          else close();
        };
        observer = new MutationObserver(() => {
          if (!next.isConnected || !next.contains(thumbnail) || (thumbnail.currentSrc || thumbnail.src) !== source) close();
        });
        observer.observe(next.closest('.legacy-grid, .project-dropdown-menu') || next.parentElement || next, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
        img.src = original;
      }, 180);
    };
    const out = (event: PointerEvent) => {
      if (anchor && (!(event.relatedTarget instanceof Node) || !anchor.contains(event.relatedTarget))) close();
    };
    const key = (event: KeyboardEvent) => { if (event.key === 'Escape') close(); };
    document.addEventListener('pointerover', over);
    document.addEventListener('pointerout', out);
    document.addEventListener('pointerdown', close, true);
    document.addEventListener('keydown', key, true);
    document.addEventListener('scroll', close, true);
    document.addEventListener('visibilitychange', close);
    window.addEventListener('resize', close);
    window.addEventListener('blur', close);
    return () => {
      close();
      document.removeEventListener('pointerover', over);
      document.removeEventListener('pointerout', out);
      document.removeEventListener('pointerdown', close, true);
      document.removeEventListener('keydown', key, true);
      document.removeEventListener('scroll', close, true);
      document.removeEventListener('visibilitychange', close);
      window.removeEventListener('resize', close);
      window.removeEventListener('blur', close);
    };
  }, []);
  return null;
}
